import { useEffect, useMemo, useState } from "react";
import { KbvPluginService } from "@/services/kbvPluginService";
import { useCompConfig } from "@/context/CompConfigContext";
import { absUrl, useSiteOrigin } from "../useSiteOrigin";
import top1 from "../assets/top-1.png";
import top2 from "../assets/top-2.png";
import top3 from "../assets/top-3.png";
import {
  formatPeriodTabLabel,
  formatPoint,
  formatWinRate,
  getAvatarUrl,
  getCurrentPeriod,
  getRecentPeriods,
  getTopRankCardStyle,
  isAccountVerified,
} from "./utils";
import "./hot-dudoan.css";

const LEADERBOARD_LIMIT = 10;
const MEDALS = { 1: top1, 2: top2, 3: top3 };

function VerifiedBadge({ size }) {
  const badgeSize = Math.max(14, Math.round(size * 0.36));
  return (
    <span
      className="kbv-hot-dudoan__verified"
      style={{ width: badgeSize, height: badgeSize }}
      aria-label="Đã xác minh"
      title="Đã xác minh"
    >
      <svg
        viewBox="0 0 12 12"
        style={{ width: badgeSize * 0.55, height: badgeSize * 0.55 }}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M2 6.5L4.5 9L10 3" />
      </svg>
    </span>
  );
}

function UserAvatar({ avatar, username, nickname, verified, size }) {
  const [imgError, setImgError] = useState(false);
  const displayName = nickname || username || "User";
  const initial = (username || displayName).charAt(0).toUpperCase();
  const avatarUrl = getAvatarUrl(avatar);
  const showPhoto = Boolean(avatarUrl) && !imgError;

  useEffect(() => {
    setImgError(false);
  }, [avatar, avatarUrl]);

  return (
    <div className="kbv-hot-dudoan__avatar" style={{ width: size, height: size }}>
      {showPhoto ? (
        <img src={avatarUrl} alt={displayName} onError={() => setImgError(true)} />
      ) : (
        <div
          className="kbv-hot-dudoan__avatar-fallback"
          style={{ fontSize: Math.round(size * 0.35) }}
          aria-label={displayName}
        >
          {initial}
        </div>
      )}
      {verified ? <VerifiedBadge size={size} /> : null}
    </div>
  );
}

function RankBadge({ rank }) {
  const num = Number(rank);
  const medal = MEDALS[num];
  if (medal) {
    return <img className="kbv-hot-dudoan__medal" src={medal} alt={`Hạng ${num}`} />;
  }
  return <span className="kbv-hot-dudoan__rank-num">{num}</span>;
}

function LeaderboardItem({ item }) {
  const displayName = item?.nickname || item?.username || "—";
  const rank = Number(item?.rank);
  const isTop3 = rank >= 1 && rank <= 3;

  return (
    <div
      className={`kbv-hot-dudoan__row${isTop3 ? " is-top" : ""}`}
      style={getTopRankCardStyle(rank)}
    >
      <div className="kbv-hot-dudoan__rank">
        <RankBadge rank={rank} />
      </div>
      <div className="kbv-hot-dudoan__body">
        <div className="kbv-hot-dudoan__user">
          <UserAvatar
            avatar={item?.avatar}
            username={item?.username}
            nickname={item?.nickname}
            verified={isAccountVerified(item)}
            size={32}
          />
          <div className="kbv-hot-dudoan__name-col">
            <p className="kbv-hot-dudoan__name">{displayName}</p>
            <span className="kbv-hot-dudoan__tips">{item.totalTips} dự đoán</span>
          </div>
        </div>
        <div className="kbv-hot-dudoan__stats">
          <div>
            <p className="kbv-hot-dudoan__stat-label">Điểm</p>
            <p className="kbv-hot-dudoan__stat-value">{formatPoint(item?.point)}</p>
          </div>
          <div>
            <p className="kbv-hot-dudoan__stat-label">Winrate</p>
            <p className="kbv-hot-dudoan__stat-value is-green">{formatWinRate(item?.winRate)}</p>
          </div>
          <div>
            <p className="kbv-hot-dudoan__stat-label">Thắng</p>
            <p className="kbv-hot-dudoan__stat-value is-green">{item?.win ?? 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HotDuDoanSideBar() {
  const origin = useSiteOrigin();
  const config = useCompConfig();
  const viewAllHref = String(config?.viewAllHref || "/kiem-tien?tab=bxh").trim() || "/kiem-tien?tab=bxh";
  const periods = useMemo(() => getRecentPeriods(3), []);
  const [activePeriod, setActivePeriod] = useState(getCurrentPeriod);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    KbvPluginService.fetchTipsterLeaderboard({
      period: activePeriod,
      page: 1,
      limit: LEADERBOARD_LIMIT,
    })
      .then((res) => {
        if (cancelled) return;
        if (res?.success) {
          setItems(res.data);
          if (res.period) setActivePeriod(res.period);
        } else {
          setItems([]);
        }
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activePeriod]);

  return (
    <aside className="kbv-home kbv-hot-dudoan" aria-label="Bảng vinh danh tipster">
      <div className="kbv-hot-dudoan__head">
        <div className="kbv-hot-dudoan__title-wrap">
          <span className="kbv-hot-dudoan__emoji" aria-hidden>
            🏆
          </span>
          <h2 className="kbv-hot-dudoan__title">Top kiếm tiền</h2>
        </div>
        <a className="kbv-hot-dudoan__more" href={absUrl(origin, viewAllHref)}>
          Xem tất cả
        </a>
      </div>

      <div className="kbv-hot-dudoan__tabs">
        {periods.map((period) => {
          const active = period === activePeriod;
          return (
            <button
              key={period}
              type="button"
              className={`kbv-hot-dudoan__tab${active ? " is-active" : ""}`}
              onClick={() => setActivePeriod(period)}
            >
              {formatPeriodTabLabel(period)}
            </button>
          );
        })}
      </div>

      <div className="kbv-hot-dudoan__list-wrap">
        <div className="kbv-hot-dudoan__list">
          {loading
            ? [1, 2, 3].map((i) => <div key={i} className="kbv-hot-dudoan__skel" />)
            : null}

          {!loading && items.length === 0 ? (
            <div className="kbv-hot-dudoan__empty">Chưa có dữ liệu bảng xếp hạng tháng này</div>
          ) : null}

          {!loading
            ? items.map((item) => (
                <div key={`${item.userId}-${item.rank}`} className="kbv-hot-dudoan__row-wrap">
                  <LeaderboardItem item={item} />
                </div>
              ))
            : null}
        </div>
      </div>
    </aside>
  );
}
